import type {
  Abilities, Action, ChoiceResult, Chosen, Cost, DecisionKind, Filter, Happening, HistoryEntry, ManaAbilityRef, ManaSymbol, Mana, Pacing, Phase, Pick, PileDest,
  PublicAnswer, PublicCard, PublicCharacteristics, PublicChoiceSpec, PublicDecision, PublicStackItem, PublicState, Restriction, Reveal, SourceCost, Target, TurnStep,
} from "../protocol";

// How the board names things: cards, actions, targets, steps and the History. Pure, and shared with
// app.ts.

export const PHASE_LABEL: Record<Phase["kind"], string> = {
  Untap: "Untap", Upkeep: "Upkeep", Draw: "Draw", Main: "Main 1",
  BeginCombat: "Begin combat", DeclareAttackers: "Declare attackers",
  DeclareBlockers: "Declare blockers", FirstStrikeDamage: "First-strike damage", CombatDamage: "Combat damage",
  EndCombat: "End of combat", Main2: "Main 2", End: "End step", Cleanup: "Cleanup",
};

// 502.4 / 514.3: nobody gets priority in the untap or cleanup steps, so neither can be a stop.
export const STOP_STEPS: { kind: Phase["kind"]; short: string; label: string }[] = [
  { kind: "Upkeep", short: "Up", label: "Upkeep" }, { kind: "Draw", short: "Dr", label: "Draw" },
  { kind: "Main", short: "M1", label: "Main 1" }, { kind: "BeginCombat", short: "BC", label: "Combat" },
  { kind: "DeclareAttackers", short: "Atk", label: "Attackers" }, { kind: "DeclareBlockers", short: "Blk", label: "Blockers" },
  { kind: "FirstStrikeDamage", short: "FS", label: "First strike" }, { kind: "CombatDamage", short: "Dmg", label: "Damage" },
  { kind: "EndCombat", short: "EC", label: "End combat" }, { kind: "Main2", short: "M2", label: "Main 2" },
  { kind: "End", short: "End", label: "End step" },
];

export const optionValue = <T>(opt: { kind: string; values: T[] } | undefined): T | null =>
  opt && opt.kind === "Some" ? opt.values[0] : null;

export function sentence(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export const isFaceDown = (c: PublicCard): boolean => c.facing.kind !== "FaceUp";

// 708.5: a face-down card's controller may look at its real face.
export function knownFace(c: PublicCard): PublicCharacteristics | null {
  switch (c.facing.kind) {
    case "FaceUp": return c.characteristics;
    case "FaceDownKnown": return c.facing.values[0];
    case "FaceDownHidden": return null;
  }
}

// 709.5: a Room permanent is named for its unlocked doors only, so it's shown by all of them.
// The name a logged card goes by on Scryfall: its face's, even when it lies face down.
export function loggedName(c: PublicCard): string {
  if (c.doors.length > 0) return c.doors.map((d) => d.name).join(" // ");
  return knownFace(c)?.name ?? cardLabel(c);
}

export function scryfallSearch(name: string): string {
  return `https://scryfall.com/search?q=${encodeURIComponent(`!"${name}"`)}`;
}

export function cardLabel(c: PublicCard): string {
  if (c.doors.length > 0) return c.doors.map((d) => d.name).join(" // ");
  if (!isFaceDown(c)) return c.characteristics.name;
  const face = knownFace(c);
  return face ? `${face.name} (face down)` : "Face-down card";
}

export const hasType = (c: PublicCard, kind: PublicCharacteristics["types"][number]["kind"]): boolean =>
  c.characteristics.types.some((t) => t.kind === kind);

export const isLandCard = (c: PublicCard): boolean => hasType(c, "Land");
export const isCreatureCard = (c: PublicCard): boolean => hasType(c, "Creature");
export const isPlaneswalkerCard = (c: PublicCard): boolean => hasType(c, "Planeswalker");

export function typeLineOf(face: PublicCharacteristics): string {
  const main = [...face.supertypes, ...face.types.filter((t) => t.kind !== "Equipment" && t.kind !== "Aura").map((t) => t.kind)].join(" ") || "—";
  return face.subtypes.length > 0 ? `${main} — ${face.subtypes.join(" ")}` : main;
}

// 614.12: what a permanent's "As this enters, choose …" chose, as its badge says it and its tip
// names it.
export function chosenLabel(c: Chosen): { text: string; tip: string } {
  switch (c.kind) {
    case "ChosenName": return { text: c.values[0], tip: `Chosen name: ${c.values[0]}` };
    case "ChosenNumber": return { text: String(c.values[0]), tip: `Chosen number: ${c.values[0]}` };
    case "ChosenCreatureType": return { text: c.values[0], tip: `Chosen creature type: ${c.values[0]}` };
    case "ChosenColor": return { text: c.values[0].kind, tip: `Chosen color: ${c.values[0].kind}` };
    case "ChosenCardType": return { text: c.values[0].kind, tip: `Chosen card type: ${c.values[0].kind}` };
    case "ChosenBasicLandType": return { text: c.values[0], tip: `Chosen basic land type: ${c.values[0]}` };
    case "ChosenKeywords": {
      const names = keywordLabels({ keywords: c.values[0] }).join(", ");
      return { text: names, tip: `Chosen abilities: ${names}` };
    }
  }
}

export function keywordLabels(face: { keywords: PublicCharacteristics["keywords"] }): string[] {
  return face.keywords.map((k) => {
    switch (k.kind) {
      case "ProtectionFrom": return `Protection from ${symbolText(k.values[0])}`;
      case "HexproofFrom": return `Hexproof from ${symbolText(k.values[0])}`;
      case "ProtectionFromCreatureType": return `Protection from ${creatureTypePlural(k.values[0])}`;
      case "ProtectionFromCardType": return `Protection from ${cardTypePlural(k.values[0].kind)}`;
      case "ProtectionFromMulticolored": return "Protection from multicolored";
      case "ProtectionFromMonocolored": return "Protection from monocolored";
      case "ProtectionFromEverything": return "Protection from everything";
      case "Ward": return wardLabel(k.values[0]);
      case "Toxic": return `Toxic ${k.values[0]}`;
      case "Firebending": return `Firebending ${k.values[0]}`;
      case "SaddlesAndCrewsAsThoughPowerGreaterBy": return `Saddles and crews as though power +${k.values[0]}`;
      default: return k.kind.replace(/([a-z])([A-Z])/g, "$1 $2");
    }
  });
}

const IRREGULAR_PLURALS: Record<string, string> = { Elf: "Elves", Dwarf: "Dwarves", Wolf: "Wolves", Werewolf: "Werewolves", Fox: "Foxes", Mouse: "Mice", Ox: "Oxen" };

function creatureTypePlural(creatureType: string): string {
  return IRREGULAR_PLURALS[creatureType] ?? `${creatureType}s`;
}

function cardTypePlural(cardType: string): string {
  return cardType === "Sorcery" ? "sorceries" : `${cardType.toLowerCase()}s`;
}

// 702.21a: "Ward {2}", or "Ward—Pay 2 life" when the cost isn't mana alone.
function wardLabel(cost: Cost[]): string {
  const [only] = cost;
  if (cost.length === 1 && only.kind === "Mana") return `Ward ${costText(only.values[0])}`;
  return `Ward—${cost.map(wardCostText).join(", ")}`;
}

function wardCostText(c: Cost): string {
  switch (c.kind) {
    case "Mana": return costText(c.values[0]);
    case "PayLife": return `Pay ${c.values[0]} life`;
    case "PayLifeEqualToPower": return "Pay life equal to its power";
    case "Discard": return c.values[0] === 1 ? "Discard a card" : `Discard ${c.values[0]} cards`;
    case "Sacrifice": return `Sacrifice a ${filterText(c.values[0]) || "permanent"}`;
    case "CollectEvidence": return `Collect evidence ${c.values[0]}`;
    case "GetPoisonCounters": return c.values[0] === 1 ? "Get a poison counter" : `Get ${c.values[0]} poison counters`;
    case "OneOf": return c.values[0].map((option, i) => {
      const [only] = option;
      const text = option.length === 1 && only.kind === "Mana" ? `Pay ${costText(only.values[0])}` : option.map(wardCostText).join(", ");
      return i === 0 ? text : text.charAt(0).toLowerCase() + text.slice(1);
    }).join(" or ");
    default: return c.kind.replace(/([a-z])([A-Z])/g, "$1 $2");
  }
}

// Templates carry no rules text, so an ability shows its kind and its source's name.
export function stackItemLabel(item: PublicStackItem): string {
  if (item.kind === "SpellItem") return item.values[0].copy ? `Copy of ${cardLabel(item.values[0].card)}` : cardLabel(item.values[0].card);
  const ability = item.values[0];
  return `${stackItemKind(item)} — ${cardLabel(ability.source)}`;
}

export type StackItemKind = "Spell" | "Activated" | "Triggered";

export function stackItemKind(item: PublicStackItem): StackItemKind {
  if (item.kind === "SpellItem") return "Spell";
  return item.values[0].kind.kind === "TriggeredAbility" ? "Triggered" : "Activated";
}

export function stackItemController(item: PublicStackItem): string {
  return item.kind === "SpellItem" ? item.values[0].caster : item.values[0].controller;
}

export function stackItemObjectId(item: PublicStackItem): number {
  return item.kind === "SpellItem" ? item.values[0].card.object_id : item.values[0].object_id;
}

// The card a stack object shows: the spell's own, or the ability's source as it last existed.
export function stackItemCard(item: PublicStackItem): PublicCard {
  return item.kind === "SpellItem" ? item.values[0].card : item.values[0].source;
}

export function stackItemTargets(item: PublicStackItem): Target[] {
  return item.values[0].targets;
}

// What every player sees the stack object called, which an auto-pass card names (server/pacing).
export function stackItemName(item: PublicStackItem): string {
  return stackItemCard(item).characteristics.name;
}

export function symbolText(s: ManaSymbol): string {
  switch (s.kind) {
    case "White": return "W";
    case "Blue": return "U";
    case "Black": return "B";
    case "Red": return "R";
    case "Green": return "G";
    case "Colorless": return "C";
    case "Generic": return String(s.values[0]);
    case "Hybrid": return `${symbolText(s.values[0])}/${symbolText(s.values[1])}`;
    case "XPip": return "X";
    case "Phyrexian": return `${symbolText(s.values[0])}/P`;
  }
}

export function costText(cost: ManaSymbol[]): string {
  return cost.length === 0 ? "{0}" : cost.map((s) => `{${symbolText(s)}}`).join("");
}

export const MANA_ORDER: [keyof Mana, string][] = [
  ["white", "W"], ["blue", "U"], ["black", "B"], ["red", "R"], ["green", "G"], ["colorless", "C"],
];

export function manaText(m: Mana): string {
  return MANA_ORDER.map(([k, sym]) => `{${sym}}`.repeat(m[k])).join("");
}

export function sourceCostHint(c: SourceCost): string {
  switch (c.kind) {
    case "TapsOnly": return "";
    case "PaysLife": return `(pay ${c.values[0]} life)`;
    case "DealsDamage": return `(${c.values[0]} damage to you)`;
    case "CostsTheCard": return "(uses up the card)";
  }
}

// 106.6: what mana with a spending restriction may pay for, as a payment's source shows it.
export function spendOnlyHint(r: Restriction): string {
  if (r.kind === "NotToCast") return `(can't cast ${spellsText(r.values[0])})`;
  const uses = spendOnlyUses(r);
  return uses === "" ? "" : `(${uses} only)`;
}

function spendOnlyUses(r: Restriction): string {
  switch (r.kind) {
    case "ToCast": return spellsText(r.values[0]);
    case "NotToCast": return `anything but ${spellsText(r.values[0])}`;
    case "ToActivate": return abilitiesText(r.values[0]);
    case "ToCastOrActivate": return `${spellsText(r.values[0])} or ${abilitiesText(r.values[1])}`;
    case "ToTurnFaceUp": {
      const words = filterText(r.values[0]);
      return `turning ${words === "" ? "permanents" : `${words} permanents`} face up`;
    }
    case "ToUnlockADoor": return "unlocking a door";
    case "ToCastFromExile": return "spells cast from exile";
    case "OneOf": return orList(r.values[0].map(spendOnlyUses).filter((w) => w !== ""));
    case "Prefers": return "";
  }
}

function orList(parts: string[]): string {
  return parts.length <= 2 ? parts.join(" or ") : `${parts.slice(0, -1).join(", ")} or ${parts[parts.length - 1]}`;
}

function spellsText(f: Filter): string {
  const words = filterText(f);
  return words === "" ? "spells" : `${words} spells`;
}

function abilitiesText(a: Abilities): string {
  if (a.kind === "Equip") return "equip abilities";
  const words = filterText(a.values[0]);
  if (words === "") return a.kind === "OfPermanents" ? "abilities of permanents" : "abilities";
  return `abilities of ${words} ${a.kind === "OfPermanents" ? "permanents" : "sources"}`;
}

const COLOR_NAMES: Partial<Record<ManaSymbol["kind"], string>> = { White: "white", Blue: "blue", Black: "black", Red: "red", Green: "green" };

// A filter as the adjectives before a noun: "legendary", "Goblin creature", "nonland". Supertypes,
// colors and subtypes come before card types, as on a type line.
function filterText(f: Filter): string {
  switch (f.kind) {
    case "Anything": return "";
    case "Creature": case "Artifact": case "Enchantment": case "Land": case "Instant": case "Sorcery": case "Planeswalker": case "Kindred":
    case "Legendary": case "Basic": case "Nonland": case "Permanent": case "Token":
      return f.kind.toLowerCase();
    case "Subtype": return f.values[0];
    case "Color": return COLOR_NAMES[f.values[0].kind] ?? "colorless";
    case "ManaValueAtMost": return `mana value ${f.values[0]} or less`;
    case "FaceDown": return "face-down";
    case "XInManaCost": return "{X}";
    case "Multicolored": return "multicolored";
    case "Not": {
      const inner = f.values[0];
      if (inner.kind === "Any" && inner.values[0].length === 5 && inner.values[0].every((g) => g.kind === "Color")) return "colorless";
      if (inner.kind === "ManaValueAtMost") return `mana value ${inner.values[0] + 1} or more`;
      return `non${filterText(inner)}`;
    }
    case "All": {
      const late = (g: Filter) => ["Creature", "Artifact", "Enchantment", "Land", "Instant", "Sorcery", "Planeswalker", "Kindred", "ManaValueAtMost", "Not"].includes(g.kind) ? 1 : 0;
      return [...f.values[0]].sort((a, b) => late(a) - late(b)).map(filterText).filter((w) => w !== "").join(" ");
    }
    case "Any": return f.values[0].map(filterText).join(" or ");
    default: return "matching";
  }
}

// What auto-pay answers a question with: the engine's suggestion for a mana payment, the mana
// abilities' or the creatures and artifacts tapped as part of one (702.51a, 702.126a), which puts the
// lands first. Null for any other question: a crew or saddle cost taps creatures by choice.
export function autoPayAnswer(spec: PublicChoiceSpec): ChoiceResult | null {
  switch (spec.kind) {
    case "ChoosePayment": return { kind: "ChosePayment", values: [spec.values[0].suggested] };
    case "ChooseToTap": return spec.values[0].part_of.kind === "Some" ? { kind: "ChoseToTap", values: [spec.values[0].suggested] } : null;
    default: return null;
  }
}

export function sameAbility(a: ManaAbilityRef, b: ManaAbilityRef): boolean {
  return a.kind === b.kind && (a.kind !== "Activated" || b.kind !== "Activated" || a.values[0] === b.values[0]);
}

// Where a pass on an empty stack goes, if every other player passes too.
export function nextStepLabel(phase: Phase["kind"]): string {
  switch (phase) {
    case "Untap":
    case "Upkeep": return "Draw";
    case "Draw": return "Main";
    case "Main": return "Combat";
    case "BeginCombat": return "Attackers";
    case "DeclareAttackers": return "Blockers";
    case "DeclareBlockers":
    case "FirstStrikeDamage": return "Damage";
    case "CombatDamage": return "End of combat";
    case "EndCombat": return "Main 2";
    case "Main2": return "End step";
    case "End":
    case "Cleanup": return "Next turn";
  }
}

// An End turn or Skip turn lasts until its turn ends, and a Resolve all while what it was asked
// over is on top of the stack (server/pacing's passing_now and passes).
export function passingNow(p: Pacing, state: PublicState): Pacing["passing"] {
  const passing = p.passing;
  const atStops: Pacing["passing"] = { kind: "AtStops", values: [] };
  switch (passing.kind) {
    case "EndingTurn":
    case "SkippingTurn": return passing.values[0] === state.turn ? passing : atStops;
    case "ResolvingStack": {
      const top = state.stack[state.stack.length - 1];
      return top && passing.values[0].includes(stackItemObjectId(top)) ? passing : atStops;
    }
    default: return passing;
  }
}

export function passingLabel(p: Pacing["passing"]): string {
  switch (p.kind) {
    case "AtStops": return "";
    case "EndingTurn": return "Ending the turn…";
    case "SkippingTurn": return "Skipping the turn…";
    case "HoldingPriority": return "Holding priority";
    case "ResolvingStack": return "Resolving the stack…";
  }
}

export function toggledStep(steps: Phase[], kind: Phase["kind"]): Phase[] {
  return steps.some((s) => s.kind === kind) ? steps.filter((s) => s.kind !== kind) : [...steps, { kind, values: [] } as Phase];
}

// The card an action is taken with, which the board offers it on.
export function actionPrimaryOid(a: Action): number | null {
  switch (a.kind) {
    case "PlayLand":
    case "PlayLandBack":
    case "TapForMana":
    case "UntapForMana":
    case "TurnFaceUp":
    case "UnlockDoor":
    case "CastSpell":
    case "CastAlternative":
    case "CastBackFace":
    case "Cycle":
    case "Suspend":
    case "Plot":
    case "ActivateAbility":
      return a.values[0];
    default:
      return null;
  }
}

export function actionTargets(a: Action): Target[] {
  switch (a.kind) {
    case "CastSpell":
    case "CastAlternative":
    case "CastBackFace": return a.values[1];
    case "ActivateAbility": return a.values[2];
    default: return [];
  }
}

export function power(c: PublicCard): number | null {
  const p = optionValue(c.characteristics.power);
  return p === null ? null : p;
}

export function toughness(c: PublicCard): number | null {
  const t = optionValue(c.characteristics.toughness);
  return t === null ? null : t;
}

export function ptLabelOf(face: PublicCharacteristics, damage: number): string {
  const p = optionValue(face.power);
  const t = optionValue(face.toughness);
  if (p === null || t === null) return "";
  return damage > 0 ? `${p}/${t} · ${damage} dmg` : `${p}/${t}`;
}

export function findOnAnyBattlefield(state: PublicState, oid: number): PublicCard | null {
  for (const p of Object.values(state.players)) {
    const c = p.battlefield[String(oid)];
    if (c) return c;
  }
  return null;
}

export function controllerOf(state: PublicState, oid: number): string | null {
  for (const p of Object.values(state.players)) if (p.battlefield[String(oid)]) return p.id;
  return null;
}

export function combatLabel(state: PublicState, oid: number): string {
  const c = findOnAnyBattlefield(state, oid);
  return c ? cardLabel(c) : `#${oid}`;
}

export function playerName(state: PublicState, pid: string): string {
  return state.players[pid]?.name ?? pid;
}

// 117.4: this many passes in a row resolve the top of the stack, or end the step.
export function livePlayers(state: PublicState): string[] {
  return state.turn_order.filter((pid) => !state.players[pid]?.lost);
}

// APNAP from the active player, the order priority goes round in (101.4).
export function priorityOrder(state: PublicState): string[] {
  const live = livePlayers(state);
  const i = Math.max(0, live.indexOf(state.active));
  return [...live.slice(i), ...live.slice(0, i)];
}

// The players who passed in a row before the one with priority: `priority_passes` of them,
// counting back round the table.
export function passedSincePriorityMoved(state: PublicState): string[] {
  const live = livePlayers(state);
  const at = live.indexOf(state.priority);
  if (at < 0) return [];
  const passed: string[] = [];
  for (let k = 1; k <= Math.min(state.priority_passes, live.length - 1); k++) {
    passed.push(live[(at - k + live.length) % live.length]!);
  }
  return passed;
}

function findViewerCard(state: PublicState, seat: string | null, oid: number): PublicCard | null {
  const me = seat ? state.players[seat] : undefined;
  if (!me) return null;
  return me.hand[String(oid)] ?? me.battlefield[String(oid)] ?? me.graveyard[String(oid)] ?? me.command_zone[String(oid)] ?? null;
}

// Log and target cards have often moved, so every zone the projection carries is searched.
export function findCardAnywhere(state: PublicState, oid: number): PublicCard | null {
  const key = String(oid);
  for (const p of Object.values(state.players)) {
    const c = p.battlefield[key] ?? p.graveyard[key] ?? p.exile[key] ?? p.command_zone[key] ?? p.hand[key];
    if (c) return c;
    // 401.5: a library's top card, while the viewer may see it.
    const top = optionValue(p.library_top);
    if (top && top.object_id === oid) return top;
  }
  for (const item of state.stack) {
    if (stackItemObjectId(item) === oid) return stackItemCard(item);
  }
  return null;
}

export function lookupCardNameAnywhere(state: PublicState, oid: number): string {
  for (const item of state.stack) {
    if (stackItemObjectId(item) === oid) return stackItemLabel(item);
  }
  const c = findCardAnywhere(state, oid);
  return c ? cardLabel(c) : `#${oid}`;
}

export function targetNameLabel(state: PublicState, t: Target): string {
  switch (t.kind) {
    case "TargetPlayer": return playerName(state, t.values[0]);
    case "TargetObject": return lookupCardNameAnywhere(state, t.values[0]);
  }
}

export function sameTarget(a: Target, b: Target): boolean {
  return a.kind === b.kind && a.values[0] === b.values[0];
}

// Where on the board a target is drawn: a seat's avatar, or an object's card.
export function targetAnchor(t: Target): string {
  return t.kind === "TargetPlayer" ? `seat:${t.values[0]}` : `obj:${t.values[0]}`;
}

export function altCastLabel(state: PublicState, seat: string | null, oid: number, idx: number): string {
  const c = findViewerCard(state, seat, oid);
  return c?.characteristics.alt_cast_labels[idx] ?? `alternative cost #${idx}`;
}

// 709.5j: a Room's door by its name.
function doorName(state: PublicState, seat: string | null, oid: number, door: number): string {
  return findViewerCard(state, seat, oid)?.doors[door]?.name ?? `door ${door + 1}`;
}

function lookupCardName(state: PublicState, seat: string | null, oid: number): string {
  const c = findViewerCard(state, seat, oid);
  return c ? cardLabel(c) : `#${oid}`;
}

// 712.11b: the face a modal double-faced card is cast as when it's cast as its back.
function backFaceName(state: PublicState, seat: string | null, oid: number): string {
  const c = findViewerCard(state, seat, oid);
  const faces = c ? optionValue(c.faces) : null;
  return faces ? faces.back : lookupCardName(state, seat, oid);
}

export function actionLabel(state: PublicState, seat: string | null, action: Action): string {
  switch (action.kind) {
    case "Concede": return "Concede";
    case "PlayLand": return `Play ${lookupCardName(state, seat, action.values[0])}`;
    case "PlayLandBack": return `Play ${lookupCardName(state, seat, action.values[0])} as a land`;
    case "TapForMana": return `Tap ${lookupCardName(state, seat, action.values[0])} for mana`;
    case "UntapForMana": return `Untap ${lookupCardName(state, seat, action.values[0])}`;
    case "TurnFaceUp": return `Turn ${lookupCardName(state, seat, action.values[0])} face up`;
    case "UnlockDoor": return `Unlock ${doorName(state, seat, action.values[0], action.values[1])}`;
    case "CastSpell": {
      const name = lookupCardName(state, seat, action.values[0]);
      const targets = action.values[1].map((t) => targetNameLabel(state, t)).join(", ");
      return targets ? `Cast ${name} → ${targets}` : `Cast ${name}`;
    }
    case "CastAlternative": {
      const [oid, tgts, idx] = action.values;
      const name = lookupCardName(state, seat, oid);
      const targets = tgts.map((t) => targetNameLabel(state, t)).join(", ");
      const how = altCastLabel(state, seat, oid, idx);
      return targets ? `Cast ${name} (${how}) → ${targets}` : `Cast ${name} (${how})`;
    }
    case "CastBackFace": {
      const name = backFaceName(state, seat, action.values[0]);
      const targets = action.values[1].map((t) => targetNameLabel(state, t)).join(", ");
      return targets ? `Cast ${name} → ${targets}` : `Cast ${name}`;
    }
    case "PassPriority": return "Pass priority";
    case "ActivateAbility": {
      const name = lookupCardName(state, seat, action.values[0]);
      const targets = action.values[2].map((t) => targetNameLabel(state, t)).join(", ");
      return targets ? `Activate ${name}'s ability → ${targets}` : `Activate ${name}'s ability`;
    }
    case "Cycle": return `Cycle ${lookupCardName(state, seat, action.values[0])}`;
    case "Suspend": return `Suspend ${lookupCardName(state, seat, action.values[0])}`;
    case "Plot": return `Plot ${lookupCardName(state, seat, action.values[0])}`;
  }
}

// An action's label without its targets: what the card menu offers before any target is picked.
export function actionHowLabel(state: PublicState, seat: string | null, action: Action): string {
  switch (action.kind) {
    case "CastSpell": return `Cast ${lookupCardName(state, seat, action.values[0])}`;
    case "CastAlternative": return `Cast ${lookupCardName(state, seat, action.values[0])} (${altCastLabel(state, seat, action.values[0], action.values[2])})`;
    case "CastBackFace": return `Cast ${backFaceName(state, seat, action.values[0])}`;
    case "ActivateAbility": {
      const n = action.values[1] + 1;
      return `Activate ${lookupCardName(state, seat, action.values[0])}'s ${n === 1 ? "first" : n === 2 ? "second" : n === 3 ? "third" : `#${n}`} ability`;
    }
    default: return actionLabel(state, seat, action);
  }
}

// A History line: its words, and the cards it names, which the board shows on hover.
export type LogPart = string | PublicCard;

type CardPart = (oid: number) => LogPart;
type TargetPart = (t: Target) => LogPart;

// The entry carries each card it names as the viewer saw it then; one they didn't see is "a card".
function historyParts(state: PublicState, entry: HistoryEntry): LogPart[] {
  const card: CardPart = (oid) => entry.cards.find((c) => c.object_id === oid) ?? "a card";
  const target: TargetPart = (t) => t.kind === "TargetPlayer" ? playerName(state, t.values[0]) : card(t.values[0]);
  const answer = entry.answer;
  switch (answer.kind) {
    case "Take": return actionParts(answer.values[0], card, target);
    case "Attackers": return answer.values[0].length === 0 ? ["attacked with nothing"]
      : ["attacked with ", ...listed(answer.values[0].map((a) => {
        const walker = optionValue(a.planeswalker);
        return [card(a.attacker), " → ", walker === null ? playerName(state, a.defender) : card(walker)];
      }))];
    case "Blockers": return answer.values[0].length === 0 ? ["blocked nothing"]
      : ["blocked ", ...listed(answer.values[0].map((b) => [card(b.attacker), " with ", card(b.blocker)]))];
    case "Chose": return pickParts(answer.values[0], card, target);
    case "StartingPlayer": return [`chose ${playerName(state, answer.values[0])} to take the first turn`];
    case "Keep": return ["kept their hand"];
    case "Mulligan": return ["took a mulligan"];
    case "Bottom": return [`put ${cardCount(answer.values[0])} on the bottom`];
    case "RepeatLoop": {
      const [asked, went] = answer.values;
      return went === 0 ? [`proposed taking their loop round ${timesRound(asked)} more`] : [`took their loop round ${timesRound(went)} more`];
    }
  }
}

function timesRound(n: number): string {
  return n === 1 ? "once" : n === 2 ? "twice" : `${n} times`;
}

function actionParts(action: Action, card: CardPart, target: TargetPart): LogPart[] {
  const at = (targets: Target[]): LogPart[] => targets.length === 0 ? [] : [" → ", ...listed(targets.map((t) => [target(t)]))];
  switch (action.kind) {
    case "PassPriority": return ["passed priority"];
    case "Concede": return ["conceded"];
    case "PlayLand": return ["played ", card(action.values[0])];
    case "PlayLandBack": return ["played ", card(action.values[0]), " as a land"];
    case "TapForMana": return ["tapped ", card(action.values[0]), " for mana"];
    case "UntapForMana": return ["untapped ", card(action.values[0])];
    case "TurnFaceUp": return ["turned ", card(action.values[0]), " face up"];
    case "CastSpell": return ["cast ", card(action.values[0]), ...at(action.values[1])];
    case "CastAlternative": {
      const [oid, targets, alt] = action.values;
      const c = card(oid);
      const how = typeof c === "string" ? undefined : c.characteristics.alt_cast_labels[alt];
      return ["cast ", c, how ? ` (${how})` : " for an alternative cost", ...at(targets)];
    }
    case "CastBackFace": return ["cast ", card(action.values[0]), ...at(action.values[1])];
    case "ActivateAbility": return ["activated ", card(action.values[0]), "'s ability", ...at(action.values[2])];
    case "Cycle": return ["cycled ", card(action.values[0])];
    case "Suspend": return ["suspended ", card(action.values[0])];
    case "Plot": return ["plotted ", card(action.values[0])];
    case "UnlockDoor": {
      const [oid, door] = action.values;
      const c = card(oid);
      const name = typeof c === "string" ? `door ${door + 1}` : c.doors[door]?.name ?? `door ${door + 1}`;
      return ["unlocked ", name, " of ", c];
    }
  }
}

function pickParts(pick: Pick, card: CardPart, target: TargetPart): LogPart[] {
  switch (pick.kind) {
    case "PickedOption": return [`chose “${pick.values[0]}”`];
    case "PickedOptional": {
      const [source, take, took] = pick.values;
      return [`${took ? "chose" : "declined"} “${take}” (${source})`];
    }
    case "PickedTriggerFrom": return ["put ", card(pick.values[0]), "'s trigger on the stack first"];
    case "PickedTarget": return ["targeted ", target(pick.values[0])];
    case "PickedNoTarget": return ["chose no target"];
    case "PickedCards": {
      const [seen, unseen] = pick.values;
      return seen.length + unseen === 0 ? ["chose none"] : ["chose ", ...cardsParts(seen, unseen, card)];
    }
    case "PickedNumber": return [`chose ${pick.values[0]}`];
    case "PickedName": return [`chose “${pick.values[0]}”`];
    case "AssignedDamage": {
      const [attacker, shares] = pick.values;
      return ["assigned ", card(attacker), "'s damage: ", ...listed(shares.map((s) => [`${s.amount} to `, target(s.to)]))];
    }
    case "PaidWith": return pick.values[0].length === 0 ? ["paid from their mana pool"]
      : ["paid with ", ...listed(pick.values[0].map((oid) => [card(oid)]))];
    case "TappedToPay": return pick.values[0].length === 0 ? ["tapped nothing"]
      : ["tapped ", ...listed(pick.values[0].map((oid) => [card(oid)]))];
    case "Arranged": {
      const piles = pick.values[0].filter((p) => p.to.kind !== "StaysInHand" && p.to.kind !== "StaysWhereMilled" && p.cards.length + p.unseen > 0);
      const milled = pick.values[0].some((p) => p.to.kind === "StaysWhereMilled");
      return piles.length === 0 ? [milled ? "left the milled cards where they were" : "kept the cards in their hand"]
        : ["put ", ...listed(piles.map((p) => [...cardsParts(p.cards, p.unseen, card), ` ${PILE_LOGGED[p.to.kind]}`]), " and ")];
    }
  }
}

const PILE_LOGGED: Record<PileDest["kind"], string> = {
  OnTop: "on top", OnBottom: "on the bottom", OnBottomInRandomOrder: "on the bottom in a random order",
  IntoHand: "into their hand", IntoGraveyard: "into their graveyard", IntoExile: "into exile",
  OntoBattlefield: "onto the battlefield", OntoBattlefieldTapped: "onto the battlefield tapped",
  OntoBattlefieldAttacking: "onto the battlefield attacking", Manifested: "onto the battlefield face down",
  Cloaked: "onto the battlefield face down with ward {2}", StaysInHand: "in their hand",
  StaysWhereMilled: "where they were milled",
};

function cardsParts(seen: number[], unseen: number, card: CardPart): LogPart[] {
  return listed([...seen.map((oid) => [card(oid)]), ...(unseen > 0 ? [[cardCount(unseen)]] : [])]);
}

function cardCount(n: number): string {
  return n === 1 ? "a card" : `${n} cards`;
}

function listed(items: LogPart[][], separator = ", "): LogPart[] {
  return items.flatMap((item, i) => i === 0 ? item : [separator, ...item]);
}

// A line of the log: whose it is, by seat and by name, and its words and cards.
export type LogLine = { seat: string; actor: string; parts: LogPart[] };

// What an entry's answer did, unless it was a pass, then each reveal carrying it out made (701.20a),
// by whoever revealed, then the damage it dealt, the life gained and lost, and the abilities it triggered.
export function entryLines(state: PublicState, entry: HistoryEntry): LogLine[] {
  return [...answerLines(state, entry), ...happenedLines(state, entry)];
}

function answerLines(state: PublicState, entry: HistoryEntry): LogLine[] {
  const answered: LogLine[] = isPass(entry.answer) ? [] : [{ seat: entry.seat, actor: entry.actor, parts: historyParts(state, entry) }];
  return [...answered, ...entry.reveals.map((r) => ({ seat: r.player, actor: playerName(state, r.player), parts: revealParts(entry, r) }))];
}

function happenedLines(state: PublicState, entry: HistoryEntry): LogLine[] {
  const card: CardPart = (oid) => entry.cards.find((c) => c.object_id === oid) ?? "a card";
  const target: TargetPart = (t) => t.kind === "TargetPlayer" ? playerName(state, t.values[0]) : card(t.values[0]);
  return entry.happened.map((h) => {
    const seat = whoseHappening(h);
    return { seat, actor: playerName(state, seat), parts: happeningParts(h, card, target) };
  });
}

// Damage and triggers are their controller's lines; a life total's change, its player's.
function whoseHappening(h: Happening): string {
  switch (h.kind) {
    case "DealtDamage": return h.values[1];
    case "Triggered": return h.values[1];
    case "GainedLife": return h.values[0];
    case "LostLife": return h.values[0];
  }
}

function happeningParts(h: Happening, card: CardPart, target: TargetPart): LogPart[] {
  switch (h.kind) {
    case "DealtDamage": {
      const [source, , to, amount, combat] = h.values;
      return [card(source), ` dealt ${amount}${combat ? " combat" : ""} damage to `, target(to)];
    }
    case "Triggered": return [card(h.values[0]), " triggered"];
    case "GainedLife": return lifeParts("gained", h.values[1], optionValue(h.values[2]), card);
    case "LostLife": return lifeParts("lost", h.values[1], optionValue(h.values[2]), card);
  }
}

function lifeParts(verb: string, amount: number, cause: number | null, card: CardPart): LogPart[] {
  const words = `${verb} ${amount} life`;
  return cause === null ? [words] : [words, " from ", card(cause)];
}

function revealParts(entry: HistoryEntry, reveal: Reveal): LogPart[] {
  return [reveal.looked_at ? "has in hand " : "revealed ", ...listed(reveal.cards.map((oid) => [entry.cards.find((c) => c.object_id === oid) ?? "a card"]))];
}

// The log's latest `n` lines, oldest first. Passing priority is most of a game's answers, the Room's
// own among them, so the log leaves them out.
export function latestLines(state: PublicState, history: HistoryEntry[], n: number): LogLine[] {
  const lines: LogLine[] = [];
  for (let i = history.length - 1; i >= 0 && lines.length < n; i--) lines.unshift(...entryLines(state, history[i]!));
  return lines.slice(-n);
}

export function isPass(answer: PublicAnswer): boolean {
  return answer.kind === "Take" && answer.values[0].kind === "PassPriority";
}

// The coin flip's choice and the mulligans, answered before the first turn (103).
export function isPregameAnswer(answer: PublicAnswer): boolean {
  return answer.kind === "StartingPlayer" || answer.kind === "Keep" || answer.kind === "Mulligan" || answer.kind === "Bottom";
}

export function stepOf(state: PublicState): TurnStep {
  return { turn: state.turn, round: state.round, active: state.active, phase: state.phase };
}

export function sameStep(a: TurnStep, b: TurnStep): boolean {
  return a.turn === b.turn && a.active === b.active && a.phase.kind === b.phase.kind;
}

// A step of the log: the players who passed priority in it, and everything else answered or done
// there, oldest first.
export type LoggedStep = { phase: Phase["kind"]; passed: string[]; lines: LogLine[] };
// A turn of the log, its steps oldest first; the pregame's answers are a turn of their own, `turn` null.
// `round` is the turn number players see (PublicState's `round`).
export type LoggedTurn = { turn: number | null; round: number; active: string; steps: LoggedStep[] };

// The History as the log tells it, oldest first: each turn, and each step anyone answered in or
// anything was done in. A pass that ends a step deals the next step's damage, so that's logged there.
export function loggedTurns(state: PublicState, history: HistoryEntry[]): LoggedTurn[] {
  const turns: LoggedTurn[] = [];
  const stepAt = (at: TurnStep, pregame: boolean): LoggedStep => {
    const number = pregame ? null : at.turn;
    let turn = turns.at(-1);
    if (!turn || turn.turn !== number) {
      turn = { turn: number, round: at.round, active: at.active, steps: [] };
      turns.push(turn);
    }
    let step = turn.steps.at(-1);
    if (!step || (!pregame && step.phase !== at.phase.kind)) {
      step = { phase: at.phase.kind, passed: [], lines: [] };
      turn.steps.push(step);
    }
    return step;
  };
  for (const e of history) {
    const step = stepAt(e.step, isPregameAnswer(e.answer));
    step.lines.push(...answerLines(state, e));
    if (isPass(e.answer) && !step.passed.includes(e.seat)) step.passed.push(e.seat);
    if (e.happened.length > 0) stepAt(e.happened_in, false).lines.push(...happenedLines(state, e));
  }
  return turns;
}

export function waitingLabel(kind: DecisionKind["kind"]): string {
  switch (kind) {
    case "Acting": return "act";
    case "DeclaringAttackers": return "declare attackers";
    case "DeclaringBlockers": return "declare blockers";
    case "Choosing": return "choose";
    case "ChoosingStartingPlayer": return "choose who takes the first turn";
    case "KeepingOrMulliganing": return "keep or mulligan";
    case "PuttingOnBottom": return "put cards on the bottom";
  }
}

// The pregame (103): who takes the first turn, then the opening hands, before any turn has begun.
export function isPregameDecision(decision: PublicDecision): boolean {
  switch (decision.kind) {
    case "ViewerChoosesStartingPlayer":
    case "ViewerKeepsOrMulligans":
    case "ViewerPutsOnBottom": return true;
    case "AnotherPlayerDecides": {
      const kind = decision.values[1].kind;
      return kind === "ChoosingStartingPlayer" || kind === "KeepingOrMulliganing" || kind === "PuttingOnBottom";
    }
    default: return false;
  }
}
